import { generateID, isNullish } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import { defineComponent, computed, ref, watch } from 'vue';
import __vue_component__ from '../buttons/ButtonComponent/ButtonComponent.vue.js';
import TransitionExpand from '../transitions/TransitionExpand/TransitionExpand.js';
import __vue_component__$1 from '../typography/Copy.vue.js';
import '@caresend/types';
import 'file-saver';
import '../../database/firebaseConfig/cloudFunctions.js';
import '../../database/encryption/model.js';
import { generateHash } from '../../database/encryption/utils/helpers.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';

var script = defineComponent({
    name: 'ListAddRemoveItem',
    components: {
        ButtonComponent: __vue_component__,
        Copy: __vue_component__$1,
        TransitionExpand,
    },
    props: {
        list: {
            type: Array,
            required: true,
        },
        setIdAsItemKey: Boolean,
        title: {
            type: String,
            default: undefined,
        },
        subtitle: {
            type: String,
            default: undefined,
        },
        itemType: {
            type: String,
            default: undefined,
        },
        hideDeleteButton: {
            type: Boolean,
        },
        containerClass: {
            type: String,
            default: undefined,
        },
        limit: {
            type: Number,
            default: undefined,
        },
        canDeleteFirstItem: Boolean,
        fullWidthAddButton: Boolean,
    },
    setup(props, { emit }) {
        const containerClassNames = computed(() => {
            const classNames = [];
            if (!props.hideDeleteButton
                && (props.list.length > 1 || props.canDeleteFirstItem))
                classNames.push('ListAddRemoveItem__container-with-button');
            if (props.containerClass)
                classNames.push(props.containerClass);
            return classNames;
        });
        /**
         * Possible updates to the list include:
         * - 1. A new item is added
         *   - The hash of the new item will not have existed in localIDsWithHashes,
         *     so a new ID and hash should be created
         * - 2. An item is removed
         *   - The hash of the item at the same index and all that follow may not match,
         *     so those keys should be updated
         * - 3. A specific item is updated
         *   - The hash of only this item will change, no update should be triggered
         */
        /** Keep a reference to DOM element IDs */
        const localIDs = ref(props.list.map(() => generateID()));
        /** Keep a reference to the IDs and the hashes of the represented items */
        const localIDsWithHashes = computed(() => localIDs.value.map((id, index) => {
            var _a;
            return ({
                id,
                hash: generateHash((_a = props.list[index]) !== null && _a !== void 0 ? _a : {}),
            });
        }));
        /**
         * Also keep a reference to the hashes mapped to the IDs of the
         * elements which they point to in case we can re-use an existing
         * element
         */
        const hashesMappedToIDs = computed(() => localIDsWithHashes.value.reduce((acc, { hash, id }) => {
            var _a;
            const ids = (_a = acc[hash]) !== null && _a !== void 0 ? _a : new Set();
            ids.add(id);
            acc[hash] = ids;
            return acc;
        }, {}));
        watch(props.list, () => {
            const newHashes = props.list.map((item, index) => {
                var _a, _b;
                return ({
                    id: (_b = (_a = localIDsWithHashes.value[index]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : generateID(),
                    hash: generateHash(item),
                });
            });
            const hashesToIDs = cloneDeep(hashesMappedToIDs.value);
            let requiresUpdate = props.list.length !== localIDs.value.length;
            const update = newHashes.map(({ hash, id }, index) => {
                const localIDWithHashes = localIDsWithHashes.value[index];
                const newIDWithHash = (newID) => {
                    requiresUpdate = true;
                    return {
                        id: newID !== null && newID !== void 0 ? newID : generateID(),
                        hash,
                    };
                };
                // If the hash does not exist, this is a new item
                if (isNullish(localIDWithHashes))
                    return newIDWithHash();
                // If the ID has changed, this might be a new item
                if (id !== localIDWithHashes.id) {
                    // Check if there is an element which the object's hash points to
                    const ids = hashesToIDs[hash];
                    /**
                     * If there is, use this old ID as the key pointer. We can
                     * treat this as the same element because the hash is identical
                     */
                    const existingID = ids ? [...ids][0] : undefined;
                    if (existingID) {
                        ids === null || ids === void 0 ? void 0 : ids.delete(localIDWithHashes.id);
                        return newIDWithHash(existingID);
                    }
                }
                return localIDWithHashes;
            });
            if (requiresUpdate) {
                localIDs.value = update.map(({ id }) => id);
            }
        }, { deep: true });
        const addButtonLabel = computed(() => {
            const maybeAnother = props.list.length ? 'another ' : '';
            const addOrSet = props.list.length === 0 ? 'Set' : 'Add';
            return `${addOrSet} ${maybeAnother}${props.itemType}`;
        });
        const add = () => {
            if (props.limit !== props.list.length) {
                emit('add');
            }
        };
        const remove = (index) => {
            emit('remove', index);
        };
        const getItemKey = (item, index) => {
            if (props.setIdAsItemKey)
                return item.id;
            const localID = localIDs.value[index];
            return localID !== null && localID !== void 0 ? localID : index;
        };
        return {
            add,
            remove,
            addButtonLabel,
            containerClassNames,
            getItemKey,
        };
    },
});

export { script as default };
